import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'

export type Ficha = components['schemas']['FichaRead']
export type FichaUpdate = components['schemas']['FichaUpdate']
export type FichaOperadorUpdate = components['schemas']['FichaOperadorUpdate']

export function useMyFicha(businessId: string | undefined) {
  return useQuery({
    queryKey: ['mi-ficha', businessId],
    queryFn: async () => (await api.get<Ficha>(`/businesses/${businessId}/mi/ficha`)).data,
    enabled: !!businessId,
  })
}

/** Partial PUT per answer (registro-entrevista-spec §3b); `page_reached` only ratchets forward server-side. */
export function useUpdateMyFicha(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: FichaUpdate) =>
      (await api.put<Ficha>(`/businesses/${businessId}/mi/ficha`, payload)).data,
    onSuccess: (data) => {
      queryClient.setQueryData(['mi-ficha', businessId], data)
      queryClient.invalidateQueries({queryKey: ['mi-onboarding', businessId]})
    },
  })
}

export function useEmployeeFicha(businessId: string | undefined, employeeId: string | undefined) {
  return useQuery({
    queryKey: ['ficha', businessId, employeeId],
    queryFn: async () => (await api.get<Ficha>(`/businesses/${businessId}/employees/${employeeId}/ficha`)).data,
    enabled: !!businessId && !!employeeId,
  })
}

/** The operator may overwrite the employee's answers: the auto-registro is input, Kairos is the record. */
export function useUpdateEmployeeFicha(businessId: string, employeeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: FichaOperadorUpdate) =>
      (await api.patch<Ficha>(`/businesses/${businessId}/employees/${employeeId}/ficha`, payload)).data,
    onSuccess: (data) => {
      queryClient.setQueryData(['ficha', businessId, employeeId], data)
    },
  })
}
